/**
 * CateringBookingSyncService — watches booking changes for linked catering
 * orders. It never writes to an order.
 *
 * RESPONSIBILITY: Observes two signal streams:
 *   1. RealtimeService.lastBookingChange — per-booking events (created /
 *      updated / moved / cancelled / released). A time or room change raises
 *      a reconciliation prompt via computeCascadeAction. A cancelled or
 *      released booking is skipped without a read: see below.
 *   2. RealtimeService.lastScheduleChange — batch calendar-webhook events
 *      emitted when Microsoft Graph / Google Calendar deltas are processed.
 *      Acts as a reconciliation nudge: if we receive one and the user has
 *      active catering orders, we refetch those orders to pick up any status
 *      changes the per-booking stream may have missed.
 *
 * THE SERVER DECIDES WHAT A CANCELLED MEETING DOES TO ITS CATERING
 * (indoor_mapping_poc-5wkp). This service used to cancel every linked order
 * itself the moment the user cancelled a booking, with no status check. That
 * overrode the vendor's policy: rdx-platform-api's
 * `CateringService.bookingLossDecision` keeps confirmed and in-preparation
 * orders for review when the vendor's `bookingCancellationAction` is
 * 'review' or its cancellation cut-off has passed, and never cancels an
 * order that is out for delivery or later. The server runs that decision on
 * the `BOOKING_UPDATED` event every booking cancellation publishes (and on
 * `CALENDAR_MEETING_CANCELLED` for a meeting cancelled in Outlook), then
 * notifies the orderer. Nothing here may pre-empt it.
 *
 * MICROSOFT/GOOGLE CALENDAR INTEGRATION (BACKEND CONTRACT, READ CAREFULLY):
 *
 *   This service assumes that rdx-platform-api's Microsoft Graph / Google
 *   Calendar webhook processors emit `bookingChanged` subscription events
 *   with the correct changeType and updated startTime/endTime fields for
 *   each affected booking when a meeting is modified externally.
 *
 *   If the backend emits ONLY `scheduleEventChanged` on calendar deltas
 *   (without per-booking fan-out), the per-booking prompt path won't fire.
 *   The `scheduleEventChanged` observer below is a safety net that reloads
 *   the current user's orders so at least the data is fresh.
 *
 *   Cancellation is not this service's job on either path. To check it:
 *     1. Create a meeting in the app with catering (vendor, items, time).
 *     2. Cancel the meeting, in the app or in Outlook.
 *     3. A draft or awaiting-approval order is cancelled. A confirmed or
 *        in-preparation order is cancelled only when the vendor cancels on
 *        booking loss and its cut-off has not passed; otherwise it stays,
 *        flagged for review. An order out for delivery is never cancelled.
 *        The orderer is told either way.
 *
 * SIGNALS:
 *   pendingReconciliations (readonly) — prompts awaiting user decision
 *   lastSyncAt (readonly)             — timestamp of the most recent cascade
 *
 * USED BY: AppComponent (bootstrap) via early effect registration; UI surfaces
 *   consume pendingReconciliations to render persistent notifications.
 *
 * AI CONTEXT: Reschedule (`auto-reschedule` decision) currently surfaces a
 * user prompt rather than silently mutating — the backend has no
 * rescheduleCateringOrder mutation yet, and cancel+recreate loses the
 * vendor's original confirmation. Wire the direct mutation in when the
 * backend ships it.
 *
 * AI CONTEXT — THE CATERING FLAG (indoor_mapping_poc-m4dz.9). Every catering
 * resolver carries `@FeatureFlag('catering')` and answers NOT_FOUND when the
 * viewer's tenant (or division) has it off. This service reacts to EVERY
 * booking change, catering or not, so without a gate a tenant with no
 * catering sent one failing request per grid drag. Nothing here goes to the
 * network unless `FeatureFlagsViewerService.is('catering')` — which is false
 * until the flag set has loaded, the same fail-closed reading the route guard
 * and `*imFeatureFlag` use.
 *
 * AI CONTEXT — THE READ. Linked orders come from the LEAN
 * `loadOrderLinksForBookingResult`, not the full per-booking read: one
 * stored order missing a non-null field the full read selects fails the
 * whole list. A failed read is non-fatal — the cascade skips that booking —
 * and is logged ONCE per booking id, because the same booking fails again on
 * every realtime tick.
 */
import { Injectable, inject, signal, effect, untracked } from '@angular/core';
import {
  CateringApiError,
  CateringApiService,
  type CateringOrderLink,
} from './catering-api.service';
import { RealtimeService, BookingChangedPayload } from './realtime.service';
import { FeatureFlagsViewerService } from './feature-flags-viewer.service';
import type { CateringOrder } from '../models/catering.model';
import { errorFromException, isErr, type ApiError } from '../models/api-result';
import {
  computeCascadeAction,
  isBookingLoss,
  CascadeDecision,
  BookingChange,
} from '../utils/catering-sync';

/** A prompt the UI should surface to the user for manual decision. */
export interface ReconciliationPrompt {
  orderId: string;
  /** `orderNumber` and `vendorName` are not known to the sync — its lean
   *  read does not select them. Look the order up by `orderId` to show them. */
  orderNumber?: number;
  vendorName?: string;
  bookingId: string;
  reason: string;
  suggestedAction: 'cancel' | 'keep-as-is' | 'review';
  newDeliveryTime?: string;
  newSpaceId?: string;
  createdAt: Date;
}

/**
 * `computeCascadeAction` declares the whole `DeliveryTarget` but reads only
 * its `spaceId`, which is all the lean read carries.
 */
function cascadeInput(
  order: CateringOrderLink,
): Pick<CateringOrder, 'status' | 'deliveryTime' | 'deliveryTarget'> {
  return {
    status: order.status,
    deliveryTime: order.deliveryTime,
    deliveryTarget: { spaceId: order.spaceId } as CateringOrder['deliveryTarget'],
  };
}

@Injectable({ providedIn: 'root' })
export class CateringBookingSyncService {
  private readonly api = inject(CateringApiService);
  private readonly realtime = inject(RealtimeService);
  private readonly flags = inject(FeatureFlagsViewerService);

  // ── Public signals ─────────────────────────────────

  private readonly _pendingReconciliations = signal<ReconciliationPrompt[]>([]);
  private readonly _lastSyncAt = signal<Date | null>(null);

  readonly pendingReconciliations = this._pendingReconciliations.asReadonly();
  readonly lastSyncAt = this._lastSyncAt.asReadonly();

  // ── Internal state ─────────────────────────────────

  /**
   * Booking ids whose linked-order read has already been logged as failed.
   * The same booking fails again on every realtime change to it, so the
   * console hears about it once. Bounded: past the cap the oldest id is
   * forgotten (a Set iterates in insertion order).
   */
  private readonly loggedReadFailures = new Set<string>();
  private static readonly LOGGED_READ_FAILURES_CAP = 200;

  // The two effects below depend on their event signal ONLY. Each handler
  // runs `untracked`: it reads the catering flag (a signal) before its first
  // await, and tracking that read would replay the last event whenever the
  // flag set loads or changes.

  /**
   * Effect that reacts to RealtimeService.lastBookingChange (covers remote
   * calendar-driven changes from Microsoft Graph / Google Calendar webhooks
   * as well as changes made by other app users).
   */
  private readonly _bookingEffect = effect(() => {
    const event = this.realtime.lastBookingChange();
    if (!event) return;
    untracked(() => void this.handleRemoteBookingChange(event));
  });

  /**
   * Safety-net effect for the Microsoft Graph / Google Calendar webhook
   * path. The backend emits scheduleEventChanged as a batch notification
   * when it processes external calendar deltas. If the per-booking
   * bookingChanged fan-out is reliable on the backend, this is a no-op;
   * if it's not, we at least reload the user's active orders so their
   * UI doesn't show stale state. Rate-limited internally.
   */
  private readonly _scheduleEffect = effect(() => {
    const evt = this.realtime.lastScheduleChange();
    if (!evt) return;
    untracked(() => void this.reconcileAfterCalendarDelta(evt.changeType));
  });

  // ── Public API ─────────────────────────────────────

  /** Dismiss a prompt from the pending list (user has resolved it). */
  dismissPrompt(orderId: string): void {
    this._pendingReconciliations.update(p => p.filter(e => e.orderId !== orderId));
  }

  /** Clear all state (e.g., when logging out). */
  reset(): void {
    this._pendingReconciliations.set([]);
    this._lastSyncAt.set(null);
    this.loggedReadFailures.clear();
    this._lastCalendarDeltaAt = 0;
  }

  // ── Calendar-delta reconciliation (safety net) ──────

  /** Minimum interval between successive reconcile calls, in ms. */
  private static readonly CALENDAR_DELTA_THROTTLE_MS = 10_000;
  private _lastCalendarDeltaAt = 0;

  /**
   * Called when the backend emits a scheduleEventChanged event. Throttles
   * to one reconcile per 10s to avoid hammering the API when a large
   * calendar delta produces many events.
   */
  private async reconcileAfterCalendarDelta(changeType: string): Promise<void> {
    if (!this.cateringEnabled()) return;
    const now = Date.now();
    if (now - this._lastCalendarDeltaAt < CateringBookingSyncService.CALENDAR_DELTA_THROTTLE_MS) {
      return;
    }
    this._lastCalendarDeltaAt = now;

    // For now, we just refresh the user's active orders so their UI stays
    // current. Per-booking cascade decisions still depend on the backend
    // emitting bookingChanged for each affected booking (see class doc).
    try {
      await this.api.loadMyOrders();
      this._lastSyncAt.set(new Date());
    } catch (err) {
      console.warn('[CateringBookingSync] calendar-delta reconcile failed:', err, { changeType });
    }
  }

  // ── Remote-event path ──────────────────────────────

  private async handleRemoteBookingChange(event: BookingChangedPayload): Promise<void> {
    if (!this.cateringEnabled()) return;
    // A cancelled or released booking is the server's to act on (see the
    // class doc), so there is nothing to read for it.
    if (isBookingLoss(event.changeType)) return;

    const orders = await this.loadLinkedOrders(event.bookingId);
    if (!orders?.length) return;

    const change: BookingChange = {
      bookingId: event.bookingId,
      changeType: event.changeType,
      startTime: event.startTime,
      endTime: event.endTime,
      spaceId: event.spaceId,
    };

    for (const order of orders) {
      const decision = computeCascadeAction(cascadeInput(order), change, {
        // cutOffMinutes lives on the vendor, which we don't always have
        // loaded here. Defaulting to 0 means the cut-off gate only catches
        // past-time cases; the backend remains authoritative for vendor-
        // specific windows and will reject late reschedules.
        cutOffMinutes: 0,
      });
      await this.executeDecision(order, decision, event.bookingId);
    }

    this._lastSyncAt.set(new Date());
  }

  private async executeDecision(
    order: CateringOrderLink,
    decision: CascadeDecision,
    bookingId: string,
  ): Promise<void> {
    switch (decision.action) {
      case 'no-op':
        return;

      case 'auto-reschedule': {
        // The backend doesn't yet expose a dedicated rescheduleCateringOrder
        // mutation (tracked as Phase 3 out-of-scope). For now, surface a prompt
        // so the user can confirm the new time themselves — strictly safer
        // than cancel+recreate, which loses the vendor's original confirmation.
        this.pushPrompt({
          orderId: order._id,
          bookingId: order.bookingId ?? bookingId,
          reason: decision.reason ?? 'Meeting time changed — confirm new delivery time',
          suggestedAction: 'review',
          newDeliveryTime: decision.newDeliveryTime?.toISOString(),
          newSpaceId: decision.newSpaceId,
          createdAt: new Date(),
        });
        return;
      }

      case 'prompt-user': {
        this.pushPrompt({
          orderId: order._id,
          bookingId: order.bookingId ?? bookingId,
          reason: decision.reason ?? 'Meeting changed — review catering',
          suggestedAction: 'review',
          newDeliveryTime: decision.newDeliveryTime?.toISOString(),
          newSpaceId: decision.newSpaceId,
          createdAt: new Date(),
        });
        return;
      }
    }
  }

  // ── Helpers ────────────────────────────────────────

  private pushPrompt(prompt: ReconciliationPrompt): void {
    // Deduplicate by orderId — only the most recent prompt per order matters
    this._pendingReconciliations.update(list => [
      prompt,
      ...list.filter(p => p.orderId !== prompt.orderId),
    ]);
  }

  /**
   * Whether the viewer's tenant has catering at all. Untracked, so a caller
   * inside a reactive context never takes a dependency on the flag set.
   */
  private cateringEnabled(): boolean {
    return untracked(() => this.flags.is('catering'));
  }

  /**
   * The orders linked to a booking, or `null` when they could not be READ —
   * never `[]` for a failure, so no caller can stamp `lastSyncAt` over a read
   * that never landed. Non-fatal either way: the cascade skips the booking.
   */
  private async loadLinkedOrders(bookingId: string): Promise<CateringOrderLink[] | null> {
    let error: ApiError;
    try {
      const result = await this.api.loadOrderLinksForBookingResult(bookingId, {
        background: true,
      });
      if (!isErr(result)) return result.data;
      error = result.error;
    } catch (cause) {
      // The Result method does not throw; this catches a bug that leaks
      // through, so it cannot surface as an unhandled rejection from an effect.
      error = errorFromException(cause);
    }
    this.warnReadFailureOnce(bookingId, error);
    return null;
  }

  /** CLAUDE.md §4 side-channel log, once per booking id (see `loggedReadFailures`). */
  private warnReadFailureOnce(bookingId: string, error: ApiError): void {
    if (this.loggedReadFailures.has(bookingId)) return;
    this.loggedReadFailures.add(bookingId);
    if (this.loggedReadFailures.size > CateringBookingSyncService.LOGGED_READ_FAILURES_CAP) {
      const oldest = this.loggedReadFailures.values().next().value;
      if (oldest !== undefined) this.loggedReadFailures.delete(oldest);
    }
    console.warn(
      '[CateringBookingSync] could not read the catering linked to a booking; its cascade is skipped (logged once per booking):',
      bookingId,
      new CateringApiError('loadOrderLinksForBooking', error),
    );
  }
}
